import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];
  connectedCallback() {
    super.connectedCallback();
    this.refs.searchPageInput.form?.addEventListener('submit', this.#handleSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.refs.searchPageInput.form?.removeEventListener('submit', this.#handleSubmit);
  }

  /**
   * Rewrites the query on submit so each term is wrapped in quotes before
   * being sent to the native search endpoint. The default fuzzy matching
   * is too permissive for this catalog: a single word query such as
   * banarasi was matching almost every product. Quoting each term
   * forces exact substring matching, which returns correctly filtered
   * results. Queries that already use advanced search syntax (quotes,
  * field scoping, or boolean operators) are left untouched.
   *
   * @param {SubmitEvent} event
   */
  #handleSubmit = (event) => {
    const input = this.refs.searchPageInput;
    const raw = input.value.trim();

    if (!raw || raw.indexOf('"') !== -1 || raw.indexOf(':') !== -1 || /\b(AND|OR|NOT)\b/.test(raw)) return;

    event.preventDefault();

    const query = raw
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => '"' + word + '"')
      .join(' AND ');

    const form = input.form;
    const url = new URL(form.action, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    window.location.assign(url.toString());
  };


  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
